<script lang="ts" setup>
    import { computed, onBeforeUnmount, ref, watch } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useChatbotError, getStreamErrorPayload } from 'composables'

    const props = defineProps<{
        error?: Error
    }>()

    const emit = defineEmits<{
        (e: 'retry'): void
        (e: 'reset'): void
    }>()

    const { t: $t } = useI18n({
        useScope: 'global',
    })

    const {
        isContextWindowError,
        isNotFoundError,
        isRateLimitError,
        isOriginNotAllowedError,
        isCreditExhaustedError,
        isConversationBlockedError,
        isBadRequestError,
        isInternalServerError,
        isGatewayConfigError,
        isRefreshableError,
    } = useChatbotError(() => props.error)

    const errorMessage = computed(() => {
        if (isGatewayConfigError.value) {
            const payload = getStreamErrorPayload(props.error)
            if (payload?.reason === 'disallowPromptTraining') {
                return $t('message.chatErrorGatewayPromptTraining', {
                    model: payload.model,
                })
            }
            if (payload?.reason === 'zeroDataRetention') {
                return $t('message.chatErrorGatewayZdr', {
                    model: payload.model,
                })
            }
            return $t('message.chatErrorGeneric')
        }
        if (isContextWindowError.value) {
            return $t('message.chatErrorContextWindow')
        }
        if (isNotFoundError.value) {
            return $t('message.chatErrorNotFound')
        }
        if (isRateLimitError.value) {
            return $t('message.chatErrorRateLimit', {
                seconds: retryCountdown.value,
            })
        }
        if (isOriginNotAllowedError.value) {
            return $t('message.chatErrorOriginNotAllowed')
        }
        if (isCreditExhaustedError.value) {
            return $t('message.chatErrorCreditExhausted')
        }
        if (isConversationBlockedError.value) {
            return $t('message.chatErrorConversationBlocked')
        }
        if (isBadRequestError.value) {
            return $t('message.chatErrorBadRequest')
        }
        if (isInternalServerError.value) {
            return $t('message.chatErrorInternalServer')
        }
        return $t('message.chatErrorGeneric', { seconds: retryCountdown.value })
    })

    const retryCountdown = ref<number>(0)
    const retryTimer = ref<ReturnType<typeof setTimeout>>()

    watch(
        () => props.error,
        (error) => {
            if (retryTimer.value) {
                clearTimeout(retryTimer.value)
            }

            if (error && isRefreshableError.value) {
                retryCountdown.value = 10
                const tick = () => {
                    retryCountdown.value--
                    if (retryCountdown.value <= 0) {
                        emit('retry')
                        return
                    }
                    retryTimer.value = setTimeout(tick, 1000)
                }
                retryTimer.value = setTimeout(tick, 1000)
            } else {
                retryCountdown.value = 0
            }
        },
        { immediate: true },
    )

    onBeforeUnmount(() => {
        if (retryTimer.value) {
            clearTimeout(retryTimer.value)
        }
    })
</script>

<template>
    <VvAlert
        :modifiers="isRefreshableError ? 'warning' : 'danger'"
        class="rounded-lg">
        <div
            class="flex gap-sm items-center"
            :class="
                isRefreshableError
                    ? 'text-warning-darken-4'
                    : 'text-danger-darken-2'
            ">
            <VvIcon
                :name="
                    isRefreshableError
                        ? 'ri:alert-line'
                        : 'ri:error-warning-line'
                "
                class="text-32 shrink-0" />
            {{ errorMessage }}
        </div>
        <template
            v-if="
                isRefreshableError ||
                (!isOriginNotAllowedError &&
                    !isCreditExhaustedError &&
                    !isGatewayConfigError)
            "
            #footer>
            <VvButton
                v-if="isRefreshableError"
                icon="ri:reset-right-line"
                modifiers="action"
                class="text-12"
                :label="$t('action.retryNow')"
                @click="$emit('retry')" />
            <VvButton
                v-else-if="
                    !isOriginNotAllowedError &&
                    !isCreditExhaustedError &&
                    !isGatewayConfigError
                "
                icon="reload"
                modifiers="action"
                :label="$t('label.newChat')"
                @click="$emit('reset')" />
        </template>
    </VvAlert>
</template>
